import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, LightbulbIcon, MessageCircleIcon, HomeIcon, AnchorIcon, ShieldIcon, StarIcon, ArrowRightIcon, PhoneIcon, CheckCircleIcon } from 'lucide-react';
import { FadeIn } from '../components/FadeIn';
import { TrustBar } from '../components/TrustBar';
import { SectionHeading } from '../components/SectionHeading';
import { Button } from '../components/Button';
import { CTASection } from '../components/CTASection';
interface HomePageProps {
  onNavigate: (page: string) => void;
}
export function HomePage({
  onNavigate
}: HomePageProps) {
  return <div className="w-full overflow-hidden" data-id="element-371">
      {/* Hero Section */}
      <section className="relative pt-32 md:pt-44 pb-12 md:pb-24 bg-white" data-id="element-372">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-373">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12" data-id="element-374">
            <div className="w-full md:w-1/2 z-10" data-id="element-375">
              {/* Mobile avatar — visible only on small screens */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.5
            }} className="md:hidden mb-5" data-id="element-376">
                <img src="https://cdn.magicpatterns.com/uploads/2uhV1wD2XTFP3d8EaQ5Sbm/260409_jdg_hypotheken_en_verzekeringen_by_robert_waasdorp-1.jpg" alt="Jordy de Gier, oprichter JDG" className="w-20 h-20 rounded-full object-cover object-top shadow-card-lg border-2 border-gold/30" data-id="element-377" />
              </motion.div>

              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-navy leading-tight mb-4 md:mb-6 tracking-tight" data-id="element-378">
                Welkom bij JDG.
                <br data-id="element-379" />
                <span className="text-xl md:text-3xl lg:text-4xl font-bold text-gold mt-1 md:mt-2 block" data-id="element-380">
                  Jouw persoonlijke adviseurs.
                </span>
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="text-base md:text-xl text-gray-600 mb-6 md:mb-8 leading-relaxed max-w-lg" data-id="element-381">
                Hypotheek- en verzekeringsadvies door heel Nederland. Geen
                standaard antwoorden, maar een oplossing die past bij jouw
                situatie.
              </motion.p>

              {/* Trust signal — prominent on mobile */}
              <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.15
            }} className="flex items-center gap-2 mb-5 md:hidden bg-warm px-4 py-2.5 rounded-brand border border-gray-100" data-id="element-382">
                <CheckCircleIcon className="w-5 h-5 text-gold flex-shrink-0" data-id="element-383" />
                <span className="text-sm text-navy font-semibold" data-id="element-384">
                  Eerste gesprek is altijd gratis en vrijblijvend
                </span>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="flex flex-col sm:flex-row gap-3 md:gap-4" data-id="element-385">
                <Button variant="primary" size="lg" onClick={() => onNavigate('contact')} className="!py-3.5 md:!py-4" data-id="element-386">
                  Plan een gratis kennismaking
                </Button>
                <Button variant="secondary" size="lg" href="tel:0232052161" icon={<PhoneIcon className="w-5 h-5" data-id="element-388" />} iconPosition="left" className="!py-3.5 md:!py-4" data-id="element-387">
                  Bel direct
                </Button>
              </motion.div>

              {/* Trust signal — desktop only */}
              <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.4
            }} className="hidden md:flex text-sm text-gray-500 mt-6 items-center gap-2 font-medium" data-id="element-389">
                <CheckCircleIcon className="w-4 h-4 text-gold" data-id="element-390" />
                Eerste gesprek is altijd gratis en vrijblijvend
              </motion.p>
            </div>

            {/* Hero image — large on desktop, compact on mobile */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="w-full md:w-1/2 relative h-[280px] md:h-[600px] rounded-2xl overflow-hidden shadow-card-lg shadow-gray-300" data-id="element-391">
              <img src="https://cdn.magicpatterns.com/uploads/2uhV1wD2XTFP3d8EaQ5Sbm/260409_jdg_hypotheken_en_verzekeringen_by_robert_waasdorp-1.jpg" alt="Jordy de Gier, oprichter JDG" loading="eager" className="absolute inset-0 w-full h-full object-cover object-[30%_center]" data-id="element-392" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent" data-id="element-393" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Indicators Bar */}
      <TrustBar data-id="element-394" />

      {/* Personal Intro Section */}
      <section className="py-20 md:py-28 bg-warm" data-id="element-395">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-396">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-397">
            <FadeIn className="w-full md:w-1/3" data-id="element-398">
              <img src="https://cdn.magicpatterns.com/uploads/9SVskVqQ26FvPC41gdyzks/260409_jdg_hypotheken_en_verzekeringen_by_robert_waasdorp-5.jpg" alt="Team JDG aan het werk" loading="lazy" className="rounded-2xl scale-110 shadow-card-lg aspect-square object-cover object-top w-full max-w-sm mx-auto" data-id="element-399" />
            </FadeIn>
            <FadeIn delay={0.2} className="w-full md:w-2/3" data-id="element-400">
              <h4 className="text-gold font-bold tracking-wider uppercase mb-2" data-id="element-401">
                Over ons
              </h4>
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-6" data-id="element-402">
                Wij kijken verder dan de standaard regeltjes
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-403">
                Een hypotheek of verzekering raakt de belangrijkste beslissingen
                in je leven. Daarom krijg je bij ons een vast aanspreekpunt,
                korte lijntjes met je eigen adviseur, en als een bank 'nee' zegt
                gaan wij op zoek naar een route die wél werkt.
              </p>
              <ul className="space-y-3 mb-8" data-id="element-404">
                <li className="flex items-center text-navy font-medium" data-id="element-405">
                  <CheckCircleIcon className="w-5 h-5 text-gold mr-3" data-id="element-406" /> 15+
                  jaar gezamenlijke ervaring in de financiële sector
                </li>
                <li className="flex items-center text-navy font-medium" data-id="element-407">
                  <CheckCircleIcon className="w-5 h-5 text-gold mr-3" data-id="element-408" /> Erkend
                  Financieel Adviseur (SEH)
                </li>
                <li className="flex items-center text-navy font-medium" data-id="element-409">
                  <CheckCircleIcon className="w-5 h-5 text-gold mr-3" data-id="element-410" />{' '}
                  Persoonlijk bereikbaar, ook 's avonds
                </li>
              </ul>

              {/* Lokaal geworteld element */}
              <button onClick={() => onNavigate('contact')} className="flex items-center gap-4 bg-white p-4 rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gold/30 transition-all duration-300 cursor-pointer w-full text-left group" data-id="element-411">
                <img src="https://cdn.magicpatterns.com/uploads/kirqiWzVxRbHNevkp7sjEZ/JDG_Nieuwsbrief_heading_goed_zonder_(1).jpg" alt="JDG Kantoor aan de Sloterweg, Badhoevedorp" className="w-20 h-14 md:w-24 md:h-16 object-cover rounded-lg flex-shrink-0" data-id="element-412" />
                <div data-id="element-413">
                  <p className="text-navy font-bold text-sm group-hover:text-gold transition-colors" data-id="element-414">
                    Gevestigd aan de Sloterweg in Badhoevedorp
                  </p>
                  <p className="text-gray-500 text-xs mt-0.5" data-id="element-415">
                    Landelijk actief, kantoor in Badhoevedorp
                  </p>
                </div>
                <ArrowRightIcon className="w-5 h-5 text-gray-300 group-hover:text-gold transition-colors ml-auto flex-shrink-0" data-id="element-416" />
              </button>

              <div className="mt-6" data-id="element-417">
                <Button variant="ghost" onClick={() => onNavigate('over-ons')} data-id="element-418">
                  Lees meer over ons
                </Button>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* USP Section */}
      <section className="py-20 md:py-28 bg-white" data-id="element-419">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-420">
          <SectionHeading title="Waarom klanten bij ons terugkomen" subtitle="Veel van onze klanten bellen ons jaren later nog steeds. Niet omdat het moet, maar omdat ze weten dat wij meedenken." data-id="element-421" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10" data-id="element-422">
            <FadeIn delay={0.1} className="bg-warm p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-423">
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-6 shadow-sm" data-id="element-424">
                <ClockIcon className="w-7 h-7 text-gold" data-id="element-425" />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-3" data-id="element-426">
                Snel schakelen
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-427">
                Geen lange wachttijden. Een afspraak is vaak dezelfde dag nog
                mogelijk, zodat je direct weet waar je aan toe bent.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-warm p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-428">
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-6 shadow-sm" data-id="element-429">
                <LightbulbIcon className="w-7 h-7 text-gold" data-id="element-430" />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-3" data-id="element-431">
                Creatieve oplossingen
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-432">
                Waar standaard banken 'nee' zeggen, kijken wij verder. Vaak is
                er meer mogelijk dan je denkt.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} className="bg-warm p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-433">
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-6 shadow-sm" data-id="element-434">
                <MessageCircleIcon className="w-7 h-7 text-gold" data-id="element-435" />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-3" data-id="element-436">
                Persoonlijk contact
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-437">
                Korte lijntjes met je eigen adviseur. Geen keuzemenu's, geen
                wisselende contactpersonen.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="py-20 md:py-28 bg-warm" data-id="element-438">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-439">
          <SectionHeading title="Waar wij je mee helpen" subtitle="Van je eerste koophuis tot een woonboot op de Amstel. En als er iets misgaat, bel je ons." data-id="element-440" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-441">
            {/* Pillar 1: Hypotheken — HERO CARD */}
            <FadeIn delay={0.1} className="flex flex-col bg-navy rounded-2xl p-8 shadow-card hover:shadow-card-hover transition-all duration-300 relative overflow-hidden" data-id="element-442">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-bl-full" data-id="element-443" />
              <HomeIcon className="w-12 h-12 text-gold mb-6 relative z-10" strokeWidth={1.5} data-id="element-444" />
              <h3 className="text-2xl font-bold text-white mb-4 relative z-10" data-id="element-445">
                Hypotheken
              </h3>
              <p className="text-gray-300 mb-8 flex-grow relative z-10" data-id="element-446">
                Voor starters, doorstromers, ondernemers en bij scheidingen. Wij
                vergelijken de voorwaarden van meer dan 30 geldverstrekkers en
                zoeken de beste deal voor jouw situatie.
              </p>
              <div className="flex flex-col gap-3 relative z-10 mt-auto" data-id="element-447">
                <Button variant="primary" onClick={() => onNavigate('hypotheken')} className="w-full" data-id="element-448">
                  Bekijk mogelijkheden
                </Button>
                <button onClick={() => onNavigate('contact')} className="text-white hover:text-gold font-medium transition-colors text-center py-2" data-id="element-449">
                  Plan een gratis gesprek
                </button>
              </div>
            </FadeIn>

            {/* Pillar 2: Woonboten */}
            <FadeIn delay={0.2} className="flex flex-col bg-white rounded-2xl p-8 border border-gray-100 shadow-card hover:shadow-card-hover transition-all duration-300 group" data-id="element-450">
              <AnchorIcon className="w-12 h-12 text-navy mb-6" strokeWidth={1.5} data-id="element-451" />
              <h3 className="text-2xl font-bold text-navy mb-4" data-id="element-452">Woonboten</h3>
              <p className="text-gray-600 mb-8 flex-grow" data-id="element-453">
                De meeste banken zeggen 'nee' tegen woonboten. Wij hebben het
                netwerk van gespecialiseerde geldverstrekkers die wél
                financieren. Van ligplaatsvergunning tot notaris, wij begeleiden
                je bij elke stap.
              </p>
              <button onClick={() => onNavigate('woonboten')} className="flex items-center text-gold font-bold group-hover:text-navy transition-colors" data-id="element-454">
                Lees meer{' '}
                <ArrowRightIcon className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-455" />
              </button>
            </FadeIn>

            {/* Pillar 3: Verzekeringen — regular card */}
            <FadeIn delay={0.3} className="flex flex-col bg-white rounded-2xl p-8 border border-gray-100 shadow-card hover:shadow-card-hover transition-all duration-300 group" data-id="element-456">
              <ShieldIcon className="w-12 h-12 text-navy mb-6" strokeWidth={1.5} data-id="element-457" />
              <h3 className="text-2xl font-bold text-navy mb-4" data-id="element-458">
                Verzekeringen
              </h3>
              <p className="text-gray-600 mb-8 flex-grow" data-id="element-459">
                Wat als er iets gebeurt? Dan bel je ons. Wij regelen de
                schadeafhandeling en zorgen dat je goed verzekerd bent. Niet
                meer, niet minder.
              </p>
              <div className="flex flex-col gap-2 mt-auto" data-id="element-460">
                <button onClick={() => onNavigate('verzekeringen')} className="flex items-center text-gold font-bold group-hover:text-navy transition-colors" data-id="element-461">
                  Lees meer{' '}
                  <ArrowRightIcon className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-462" />
                </button>
                <a href="tel:0232052161" className="flex items-center text-sm text-gray-400 hover:text-navy transition-colors mt-1" data-id="element-463">
                  <PhoneIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-464" />
                  Schade? Bel direct
                </a>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Trust / Reviews Section */}
      <section className="py-20 md:py-28 bg-white relative overflow-hidden" data-id="element-465">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[400px] text-navy/5 font-serif leading-none select-none pointer-events-none" data-id="element-466">
          "
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10" data-id="element-467">
          <SectionHeading title="Wat klanten over ons zeggen" data-id="element-468" />

          <div className="flex overflow-x-auto md:grid md:grid-cols-2 gap-4 md:gap-6 pb-8 md:pb-0 hide-scrollbar snap-x snap-mandatory -mx-4 px-4 md:mx-0 md:px-0" data-id="element-469">
            <div className="w-[calc(100vw-3rem)] flex-shrink-0 md:w-auto bg-warm p-6 md:p-8 rounded-2xl shadow-card snap-center border border-gray-100" data-id="element-470">
              <div className="flex gap-1 mb-4" data-id="element-471">
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-472" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-473" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-474" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-475" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-476" />
              </div>
              <p className="text-gray-700 italic mb-6 leading-relaxed text-sm md:text-base" data-id="element-477">
                "Jordy heeft ons fantastisch geholpen bij onze eerste woning.
                Snel, duidelijk en altijd goed bereikbaar. Geen vraag was te
                veel."
              </p>
              <p className="font-bold text-navy" data-id="element-478">Sander & Lisa</p>
              <p className="text-sm text-gray-500" data-id="element-479">Starters</p>
            </div>

            <div className="w-[calc(100vw-3rem)] flex-shrink-0 md:w-auto bg-warm p-6 md:p-8 rounded-2xl shadow-card snap-center border border-gray-100" data-id="element-480">
              <div className="flex gap-1 mb-4" data-id="element-481">
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-482" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-483" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-484" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-485" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-486" />
              </div>
              <p className="text-gray-700 italic mb-6 leading-relaxed text-sm md:text-base" data-id="element-487">
                "Als ZZP'er dacht ik dat een hypotheek lastig zou worden. Jordy
                dacht out-of-the-box en had binnen een week een akkoord bij een
                bank."
              </p>
              <p className="font-bold text-navy" data-id="element-488">Mark</p>
              <p className="text-sm text-gray-500" data-id="element-489">Ondernemer</p>
            </div>

            <div className="w-[calc(100vw-3rem)] flex-shrink-0 md:w-auto bg-warm p-6 md:p-8 rounded-2xl shadow-card snap-center border border-gray-100" data-id="element-490">
              <div className="flex gap-1 mb-4" data-id="element-491">
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-492" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-493" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-494" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-495" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-496" />
              </div>
              <p className="text-gray-700 italic mb-6 leading-relaxed text-sm md:text-base" data-id="element-497">
                "Voor de financiering van onze woonboot kregen we overal 'nee'
                te horen, totdat we bij Jordy kwamen. Zeer deskundig in deze
                niche!"
              </p>
              <p className="font-bold text-navy" data-id="element-498">Familie de Vries</p>
              <p className="text-sm text-gray-500" data-id="element-499">Woonboot</p>
            </div>

            <div className="w-[calc(100vw-3rem)] flex-shrink-0 md:w-auto bg-warm p-6 md:p-8 rounded-2xl shadow-card snap-center border border-gray-100" data-id="element-500">
              <div className="flex gap-1 mb-4" data-id="element-501">
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-502" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-503" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-504" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-505" />
                <StarIcon className="w-5 h-5 fill-gold text-gold" data-id="element-506" />
              </div>
              <p className="text-gray-700 italic mb-6 leading-relaxed text-sm md:text-base" data-id="element-507">
                "Na mijn scheiding zat ik met veel onzekerheid. Jordy bracht
                rust en overzicht, en zorgde dat ik in het huis kon blijven
                wonen."
              </p>
              <p className="font-bold text-navy" data-id="element-508">Emma</p>
              <p className="text-sm text-gray-500" data-id="element-509">Scheiding</p>
            </div>
          </div>

          <FadeIn delay={0.2} className="text-center mt-12" data-id="element-510">
            <a href="https://www.advieskeuze.nl/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-navy font-medium transition-colors" data-id="element-511">
              Bekijk alle reviews op Advieskeuze
              <span className="text-navy font-bold" data-id="element-512">9.8/10</span>
              <ArrowRightIcon className="w-4 h-4" data-id="element-513" />
            </a>
          </FadeIn>
        </div>
      </section>

      {/* Final CTA */}
      <CTASection title="Benieuwd wat wij voor je kunnen betekenen?" subtitle="Plan een gratis kennismakingsgesprek. Wij nemen de tijd om je situatie te begrijpen." buttonText="Plan een gratis kennismaking" onNavigate={onNavigate} page="contact" variant="dark" data-id="element-514" />
    </div>;
}